<template>
	<el-col v-if="toolbarVisible" :span="24" class="toolbar toolbar--premium">
		<el-form :inline="true" class="toolbar-form" :class="{ 'toolbar-form--single-line': toolbarCompactSingleLine }" @submit.native.prevent>
			<slot name="prefix"></slot>
			<div class="toolbar-search-row" v-if="!searchList.length && !$scopedSlots.prefix && !$slots.prefix">
				<el-form-item class="toolbar-search-item">
					<el-input v-model="searchVal" placeholder="关键字" :style="fieldStyle({ label: '关键字' })" @keyup.enter.native="triggerQueryByEnter"></el-input>
				</el-form-item>
				<!-- 查询跟在筛选项后，能放下就同一行。最后修改：2026-09-15 -->
				<el-form-item v-if="queryToolbarButton" class="toolbar-btn-item toolbar-btn-item--query">
					<el-button
						:type="resolveButtonType(queryToolbarButton)"
						:plain="resolveButtonPlain(queryToolbarButton)"
						:icon="resolveButtonIcon(queryToolbarButton)"
						:loading="!!queryToolbarButton.loading"
						@click="callFunc(queryToolbarButton)">{{ queryToolbarButton.name }}</el-button>
				</el-form-item>
				<slot name="suffix"></slot>
			</div>
			<div class="toolbar-search-row" v-else-if="searchList.length">
				<el-form-item
					v-for="(item, index) in searchList"
					:key="item.key || index"
					:label="resolveToolbarLabel(item)"
					class="toolbar-search-item"
				>
					<el-date-picker :type="item.type" v-model="searchObj[item.key]" :placeholder="fieldPlaceholder(item)"
						v-if="['month', 'date', 'datetime', 'daterange', 'datetimerange'].includes(item.type)"
						:value-format="item.format || (item.type === 'month' ? 'yyyy-MM' : (item.type.includes('time') ? 'yyyy-MM-dd HH:mm:ss' : 'yyyy-MM-dd'))"
						:style="fieldStyle(item)"
						:picker-options="item.pickerOptions"></el-date-picker>

					<el-autocomplete v-else-if="item.type === 'search'" v-model="searchObj[item.key]"
						:fetch-suggestions="(queryString, cb) => suggestionsFetcher(item, queryString, cb)"
						:placeholder="fieldPlaceholder(item)"
						:style="fieldStyle(item)"></el-autocomplete>

					<el-cascader v-else-if="item.type === 'cascader'" v-model="searchObj[item.key]"
						:options="item.options" :props="{ value: 'value', label: 'label', children: 'children' }"
						:placeholder="fieldPlaceholder(item)" clearable
						:style="fieldStyle(item)"></el-cascader>

					<el-select v-else-if="item.type === 'select'" v-model="searchObj[item.key]"
						:placeholder="fieldPlaceholder(item)" :filterable="item.filterable !== false" :clearable="item.clearable"
						:multiple="item.multiple" :style="fieldStyle(item)">
						<el-option
							v-for="option in (item.options || [])"
							:key="String(option.value) + '-' + (option.label || '')"
							:label="option.label"
							:value="option.value"></el-option>
					</el-select>

					<el-input v-model="searchObj[item.key]" :placeholder="fieldPlaceholder(item)" v-else
						:style="fieldStyle(item)"
						@keyup.enter.native="triggerQueryByEnter"></el-input>
				</el-form-item>
				<!-- 查询跟在筛选项后，能放下就同一行。最后修改：2026-09-15 -->
				<el-form-item v-if="queryToolbarButton" class="toolbar-btn-item toolbar-btn-item--query">
					<el-button
						:type="resolveButtonType(queryToolbarButton)"
						:plain="resolveButtonPlain(queryToolbarButton)"
						:icon="resolveButtonIcon(queryToolbarButton)"
						:loading="!!queryToolbarButton.loading"
						@click="callFunc(queryToolbarButton)">{{ queryToolbarButton.name }}</el-button>
				</el-form-item>
				<slot name="suffix"></slot>
			</div>

			<!-- 增删改/更多操作靠右；与查询分离，避免查询被整组挤到下一行。最后修改：2026-09-15 -->
			<div
				v-if="toolbarActionsVisible"
				class="toolbar-actions"
				:class="{ 'toolbar-actions--inline': !buttonGroups.useGrouped }"
			>
				<div class="toolbar-primary-actions" :class="{ 'toolbar-btn-row': !buttonGroups.useGrouped }">
					<el-form-item
						v-for="(item, btnIdx) in actionToolbarButtons"
						v-bind:key="toolbarBtnKey(item, btnIdx)"
						class="toolbar-btn-item">
						<el-button
							:type="resolveButtonType(item)"
							:plain="resolveButtonPlain(item)"
							:icon="resolveButtonIcon(item)"
							:class="resolveButtonToneClass(item)"
							:loading="!!item.loading"
							@click="callFunc(item)">{{ item.name }}</el-button>
					</el-form-item>
				</div>

				<el-form-item
					v-if="buttonGroups.useGrouped && buttonGroups.overflowSections.length"
					class="toolbar-btn-item toolbar-btn-item--overflow">
					<el-popover
						v-model="overflowVisible"
						placement="bottom-end"
						trigger="click"
						popper-class="toolbar-overflow-popover"
						:visible-arrow="false">
						<div
							class="toolbar-overflow-panel"
							:class="{ 'toolbar-overflow-panel--scrollable': overflowItemCount > 9 }">
							<div class="toolbar-overflow-mosaic">
								<div
									v-for="section in buttonGroups.overflowSections"
									:key="section.key"
									class="toolbar-overflow-section"
									:class="'toolbar-overflow-section--' + section.key">
									<div class="toolbar-overflow-section__label">{{ section.title }}</div>
									<div class="toolbar-overflow-grid">
										<button
											v-for="(item, idx) in section.buttons"
											:key="toolbarBtnKey(item, idx)"
											type="button"
											class="toolbar-overflow-tile"
											:class="overflowItemClass(item)"
											:style="overflowTileStyle(item)"
											:disabled="!!item.loading"
											:title="item.name"
											@click="handleOverflowClick(item)">
											<span class="toolbar-overflow-tile__icon-wrap">
												<i :class="resolveToolbarOverflowIcon(item)"></i>
												<i v-if="item.loading" class="el-icon-loading toolbar-overflow-tile__loading"></i>
											</span>
											<span class="toolbar-overflow-tile__text">{{ item.name }}</span>
										</button>
									</div>
								</div>
							</div>
						</div>
						<el-button slot="reference" plain class="toolbar-overflow-trigger">
							<i class="el-icon-s-operation"></i>
							<span>更多操作</span>
							<i class="el-icon-arrow-down toolbar-overflow-trigger__caret"></i>
						</el-button>
					</el-popover>
				</el-form-item>

				<slot name="buttonSlot"></slot>
			</div>
		</el-form>
	</el-col>
</template>
<script>
import { isQueryToolbarAction, resetParentListPage } from '../utils/resetListPage'
import { resolveToolbarFieldStyle, resolveToolbarFieldPlaceholder, shouldToolbarSingleLine, normalizeToolbarLabel } from '../utils/toolbarFieldWidth'
import {
	groupToolbarButtons,
	resolveToolbarBtnProps,
	isDangerToolbarAction,
	isImportExportToolbarAction,
	resolveToolbarBtnIcon,
	resolveToolbarOverflowIcon,
} from '../utils/toolbarButtonGroups'

export default {
	name: 'Toolbar',
	data() {
		return {
			searchVal: '',
			searchObj: {},
			overflowVisible: false,
		}
	},
	props: {
		buttonList: {
			type: Array,
			default: () => [],
		},
		searchList: {
			type: Array,
			default: () => [],
		},
		/** 超过该数量时启用分组工具栏，设为 0 则始终分组。创建于 2026-08-28 */
		groupThreshold: {
			type: Number,
			default: 6,
		},
	},
	computed: {
		toolbarVisible() {
			const hasButtons = this.buttonList != null && this.buttonList.length > 0
			const hasSearch = this.searchList != null && this.searchList.length > 0
			const hasPrefix = !!(this.$scopedSlots.prefix || this.$slots.prefix)
			return hasButtons || hasSearch || hasPrefix
		},
		buttonGroups() {
			return groupToolbarButtons(this.buttonList, { threshold: this.groupThreshold })
		},
		allToolbarButtons() {
			if (this.buttonGroups.useGrouped) {
				return this.buttonGroups.primaryButtons
			}
			return this.buttonGroups.inlineButtons
		},
		/** 是否展示搜索区（关键字或 searchList）。创建于 2026-09-15 */
		hasSearchArea() {
			return this.searchList.length > 0 ||
				(!this.searchList.length && !(this.$scopedSlots.prefix || this.$slots.prefix))
		},
		queryToolbarButton() {
			return (this.allToolbarButtons || []).find((item) => !item.IsHide && isQueryToolbarAction(item)) || null
		},
		actionToolbarButtons() {
			if (!this.hasSearchArea || !this.queryToolbarButton) {
				return this.allToolbarButtons || []
			}
			return (this.allToolbarButtons || []).filter((item) => !item.IsHide && !isQueryToolbarAction(item))
		},
		/** 导出等操作区是否渲染（不含查询）。创建于 2026-09-15 */
		toolbarActionsVisible() {
			const hasActions = (this.actionToolbarButtons || []).length > 0
			const hasOverflow = !!(this.buttonGroups.useGrouped && this.buttonGroups.overflowSections && this.buttonGroups.overflowSections.length)
			const hasButtonSlot = !!(this.$scopedSlots.buttonSlot || this.$slots.buttonSlot)
			return hasActions || hasOverflow || hasButtonSlot
		},
		toolbarCompactSingleLine() {
			if (this.$scopedSlots.prefix || this.$slots.prefix) return false
			const searchCount = this.searchList.length || 1
			return shouldToolbarSingleLine(searchCount)
		},
		overflowItemCount() {
			return (this.buttonGroups.overflowSections || []).reduce((sum, section) => {
				return sum + (section.buttons ? section.buttons.length : 0)
			}, 0)
		},
	},
	watch: {
		searchList: {
			deep: true,
			handler(newVal) {
				if (!newVal || !newVal.length) return
				const expectedKeys = newVal.map((e) => e.key)
				const currentKeys = Object.keys(this.searchObj || {})
				const structureChanged =
					expectedKeys.length !== currentKeys.length ||
					expectedKeys.some((k) => !Object.prototype.hasOwnProperty.call(this.searchObj, k))
				if (structureChanged) {
					this.initSearchObj(newVal)
					return
				}
				newVal.forEach((element) => {
					if (element.type === 'select') {
						if (element.options && element.options.length > 0) {
							const cur = this.searchObj[element.key]
							const empty = cur === undefined || cur === null || cur === ''
							if (element.initValue !== undefined && (empty || element.forceInit)) {
								this.$set(this.searchObj, element.key, element.initValue)
							} else if (element.init === true && empty) {
								this.$set(this.searchObj, element.key, element.options[0].value)
							}
						}
					} else if (element.type === 'search') {
						if (element.initValue !== undefined && !this.searchObj[element.key]) {
							this.$set(this.searchObj, element.key, element.initValue)
						}
					} else if (element.type === 'month' || element.type === 'date' || element.type === 'datetime' || element.type === 'daterange' || element.type === 'datetimerange') {
						if (element.initValue !== undefined && !this.searchObj[element.key]) {
							this.$set(this.searchObj, element.key, element.initValue)
						}
					}
				})
			},
		},
	},
	mounted() {
		if (this.searchList.length) {
			this.initSearchObj(this.searchList)
		}
	},
	methods: {
		isDangerToolbarAction,
		resolveToolbarOverflowIcon,
		overflowItemClass(item) {
			return {
				'is-danger': isDangerToolbarAction(item),
				'is-warning': isImportExportToolbarAction(item) && !isDangerToolbarAction(item),
			}
		},
		overflowTileStyle(item) {
			const func = String(item && item.Func || '').toLowerCase()
			// 学生照片 ZIP 导入/导出与配对按钮并排，不占满整行
			if (func === 'exportstudentphotoszip' || func === 'importstudentphotoszip') {
				return null
			}
			const name = String(item && item.name || '')
			if (name.length > 9) {
				return { gridColumn: 'span 2' }
			}
			return null
		},
		fieldStyle(item) {
			return resolveToolbarFieldStyle(item || {})
		},
		fieldPlaceholder(item) {
			return resolveToolbarFieldPlaceholder(item || {})
		},
		/** 筛选项标签：与 toolbar-filter-bar 一致，默认不带冒号。最后修改：2026-08-29 */
		resolveToolbarLabel(item) {
			if (!item) return ''
			return normalizeToolbarLabel(item.label, { showLabelColon: item.showLabelColon === true })
		},
		resolveButtonType(item) {
			return resolveToolbarBtnProps(item).type
		},
		resolveButtonPlain(item) {
			return resolveToolbarBtnProps(item).plain
		},
		resolveButtonToneClass(item) {
			const tone = resolveToolbarBtnProps(item).tone
			return tone ? 'toolbar-btn-tone--' + tone : ''
		},
		resolveButtonIcon(item) {
			return resolveToolbarBtnIcon(item)
		},
		toolbarBtnKey(item, btnIdx) {
			return 'toolbar_btn_' + btnIdx + '_' + (item.id != null ? item.id : (item.name || '') + (item.Func || ''))
		},
		handleOverflowClick(item) {
			if (!item || item.loading) return
			this.overflowVisible = false
			this.callFunc(item)
		},
		initSearchObj(searchList) {
			const temp = {}
			;(searchList || []).forEach((element) => {
				if (element.type === 'month') {
					if (element.initValue !== undefined) {
						temp[element.key] = element.initValue
					} else if (element.init !== false) {
						const now = new Date()
						const year = now.getFullYear()
						const month = String(now.getMonth() + 1).padStart(2, '0')
						temp[element.key] = `${year}-${month}`
					}
				} else if (element.type === 'date' || element.type === 'datetime') {
					if (element.initValue !== undefined) {
						temp[element.key] = element.initValue
					} else if (element.init !== false) {
						const now = new Date()
						const year = now.getFullYear()
						const month = String(now.getMonth() + 1).padStart(2, '0')
						const day = String(now.getDate()).padStart(2, '0')
						if (element.type === 'datetime') {
							const hours = String(now.getHours()).padStart(2, '0')
							const minutes = String(now.getMinutes()).padStart(2, '0')
							const seconds = String(now.getSeconds()).padStart(2, '0')
							temp[element.key] = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
						} else {
							temp[element.key] = `${year}-${month}-${day}`
						}
					}
				} else if (element.type === 'daterange' || element.type === 'datetimerange') {
					if (element.initValue !== undefined) {
						temp[element.key] = element.initValue
					} else if (element.init !== false) {
						const now = new Date()
						const year = now.getFullYear()
						const month = String(now.getMonth() + 1).padStart(2, '0')
						const day = String(now.getDate()).padStart(2, '0')
						const d = `${year}-${month}-${day}`
						if (element.type === 'datetimerange') {
							temp[element.key] = [`${d} 00:00:00`, `${d} 23:59:59`]
						} else {
							temp[element.key] = [d, d]
						}
					}
				} else if (
					element.type === 'select' &&
					element.options &&
					element.options.length > 0
				) {
					if (element.initValue !== undefined) {
						temp[element.key] = element.initValue
					} else if (element.init === true) {
						temp[element.key] = element.options[0].value
					}
				} else {
					temp[element.key] = ''
				}
			})
			this.searchObj = temp
		},
		suggestionsFetcher(item, queryString, cb) {
			if (!item || !item.options || !Array.isArray(item.options)) {
				return cb([])
			}
			const q = String(queryString || '').toLowerCase()
			const results = item.options
				.filter((o) => {
					const label = (o.label || '').toString().toLowerCase()
					const value = (o.value || '').toString().toLowerCase()
					return q === '' || label.indexOf(q) !== -1 || value.indexOf(q) !== -1
				})
				.map((o) => (o.label || o.value || ''))
			cb(results)
		},
		triggerQueryByEnter() {
			const queryBtn = (this.buttonList || []).find((btn) => !btn.IsHide && isQueryToolbarAction(btn))
			if (queryBtn) {
				this.callFunc(queryBtn)
			}
		},
		callFunc(item) {
			item.search = this.searchVal
			if (this.searchList.length) {
				item.search = { ...this.searchObj }
			}
			if (isQueryToolbarAction(item)) {
				resetParentListPage(this)
			}
			this.$emit('callFunction', item)
		},
	},
}
</script>

<style scoped>
.toolbar {
	width: 100%;
	max-width: 100%;
	box-sizing: border-box;
}

.toolbar /deep/ .el-form--inline.toolbar-form {
	display: flex;
	flex-wrap: wrap;
	align-items: center;
}

.toolbar /deep/ .el-form--inline .el-form-item {
	margin-right: 0;
	margin-bottom: 0;
}

.toolbar /deep/ .toolbar-search-row {
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 8px 20px;
}

.toolbar /deep/ .toolbar-search-item.el-form-item {
	display: inline-flex !important;
	align-items: center;
	flex-wrap: nowrap;
}

.toolbar /deep/ .toolbar-search-item .el-form-item__label {
	float: none !important;
	display: inline-flex;
	align-items: center;
	padding: 0 8px 0 0 !important;
	line-height: 36px;
	height: 36px;
}

.toolbar /deep/ .toolbar-search-item .el-form-item__content {
	margin-left: 0 !important;
}

@media screen and (max-width: 768px) {
	.toolbar /deep/ .toolbar-search-row {
		display: flex;
		flex-direction: column;
		align-items: stretch;
		width: 100%;
		margin-bottom: 4px;
	}

	.toolbar /deep/ .toolbar-search-item {
		width: 100% !important;
		margin-bottom: 8px !important;
	}

	.toolbar /deep/ .toolbar-search-item .el-form-item__content {
		width: 100%;
	}

	.toolbar /deep/ .toolbar-search-item .el-form-item__label {
		padding-right: 6px;
	}

	.toolbar /deep/ .el-input,
	.toolbar /deep/ .el-select,
	.toolbar /deep/ .el-cascader,
	.toolbar /deep/ .el-autocomplete,
	.toolbar /deep/ .el-date-editor {
		width: 100% !important;
		max-width: 100% !important;
	}
}
</style>
